import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';

const breakpoints = {
  mobile: '768px',
  tablet: '1024px',
};

const Page = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 0;
  min-height: 100%;
  background: #f5f7fb;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const SidePanel = styled.aside`
  background: #ffffff;
  border-right: 1px solid #e5e7eb;
  padding: 1rem 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (max-width: ${breakpoints.tablet}) {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    flex-direction: row;
    overflow-x: auto;
    padding: 0.75rem;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  color: #4b5563;
  padding: 0.4rem 0.5rem 0.55rem;
`;

const SectionBody = styled.div`
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 10px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const EmptySection = styled.div`
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 10px;
  padding: 1.25rem 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.5rem;
`;

const EmptyStarIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #d1d5db;

  & svg { width: 16px; height: 16px; }
`;

const EmptyTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: #4b5563;
`;

const EmptyDesc = styled.div`
  font-size: 0.7rem;
  color: #9ca3af;
  line-height: 1.45;
`;

const SmallCard = styled.button`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem;
  background: #ffffff;
  border: 1px solid #f3f4f6;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    border-color: #bfdbfe;
    background: #eff6ff;
  }
`;

const SmallIcon = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ bg }) => bg || '#f3f4f6'};
  color: ${({ color }) => color || '#374151'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.65rem;
  font-weight: 700;
  overflow: hidden;

  & svg { max-width: 22px; max-height: 18px; width: auto !important; height: auto !important; }
`;

const SmallText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const SmallName = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SmallSub = styled.span`
  font-size: 0.7rem;
  color: #6b7280;
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ status }) =>
    status === 'ok' ? '#22c55e' : status === 'warn' ? '#f59e0b' : status === 'error' ? '#ef4444' : '#9ca3af'};
  flex-shrink: 0;
`;

const StarBtn = styled.button`
  width: 22px;
  height: 22px;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ on }) => (on ? '#f59e0b' : '#cbd5e1')};
  flex-shrink: 0;

  &:hover { color: #f59e0b; }
`;

const LogPanel = styled.div`
  background: #0f172a;
  border-radius: 10px;
  padding: 0.7rem 0.65rem 0.55rem;
  font-family: 'SF Mono', Menlo, Consolas, 'Courier New', monospace;
  font-size: 0.68rem;
  line-height: 1.55;
  color: #cbd5e1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  max-height: 220px;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #334155; border-radius: 2px; }
`;

const LogTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.62rem;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-bottom: 0.4rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid #1e293b;
`;

const LogTitleDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 6px rgba(34, 197, 94, 0.6);
`;

const LogRow = styled.div`
  display: flex;
  gap: 0.4rem;
  align-items: baseline;
  word-break: break-all;
`;

const LogTime = styled.span`
  color: #64748b;
  flex-shrink: 0;
  font-size: 0.6rem;
`;

const LogTag = styled.span`
  font-weight: 700;
  color: ${({ tone }) =>
    tone === 'ok' ? '#4ade80'
    : tone === 'warn' ? '#fbbf24'
    : tone === 'error' ? '#f87171'
    : '#60a5fa'};
  flex-shrink: 0;
`;

const LogMsg = styled.span`
  color: #e2e8f0;
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.55rem 0.5rem;
  background: #ffffff;
  border: 1px dashed #d1d5db;
  color: #4b5563;
  border-radius: 10px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 600;
  margin-top: auto;
  white-space: nowrap;

  &:hover {
    border-color: #2563eb;
    color: #2563eb;
    background: #eff6ff;
  }

  & svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const Main = styled.main`
  padding: 2.5rem 2rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 1.5rem 1rem;
  }
`;

const Hero = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  max-width: 720px;
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 1rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const FilterTab = styled.button`
  padding: 0.4rem 0.9rem;
  border: none;
  border-radius: 999px;
  background: ${({ active }) => (active ? '#eff6ff' : 'transparent')};
  color: ${({ active }) => (active ? '#1d4ed8' : '#6b7280')};
  font-size: 0.82rem;
  font-weight: ${({ active }) => (active ? 700 : 500)};
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover {
    background: ${({ active }) => (active ? '#dbeafe' : '#f3f4f6')};
    color: ${({ active }) => (active ? '#1d4ed8' : '#374151')};
  }
`;

const ControlRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const SortSelect = styled.select`
  padding: 0.45rem 1.85rem 0.45rem 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat;
  background-position: right 0.65rem center;
  background-size: 12px 12px;

  &:hover { border-color: #d1d5db; }
  &:focus { outline: none; border-color: #2563eb; }
`;

const ViewToggleGroup = styled.div`
  display: flex;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 2px;
`;

const ViewToggleBtn = styled.button`
  width: 32px;
  height: 30px;
  border: none;
  background: ${({ active }) => (active ? '#2563eb' : 'transparent')};
  color: ${({ active }) => (active ? '#ffffff' : '#9ca3af')};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover {
    background: ${({ active }) => (active ? '#2563eb' : '#f3f4f6')};
    color: ${({ active }) => '#ffffff'};
    color: ${({ active }) => (active ? '#ffffff' : '#374151')};
  }

  & svg { width: 14px; height: 14px; }
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.7rem;
  font-weight: 700;
  color: #111827;
`;

const HeroDesc = styled.p`
  margin: 0;
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: ${({ list }) => (list ? '1fr' : 'repeat(4, 1fr)')};
  gap: 1rem;
  width: 100%;
  max-width: 1200px;

  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: ${({ list }) => (list ? '1fr' : 'repeat(2, 1fr)')};
  }

  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.button`
  position: relative;
  background: #ffffff;
  border: 2px solid ${({ active }) => (active ? '#2563eb' : '#e5e7eb')};
  border-radius: 14px;
  padding: 1.25rem 1rem 1rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.65rem;
  transition: all 0.15s ease;
  min-height: 240px;

  &:hover {
    border-color: ${({ active }) => (active ? '#2563eb' : '#93c5fd')};
    transform: translateY(-2px);
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.08);
  }
`;

const RecBadge = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
`;

const StarTopRight = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 26px;
  height: 26px;
  border: none;
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${({ on }) => (on ? '#f59e0b' : '#d1d5db')};
  border-radius: 6px;

  &:hover { background: #f9fafb; color: #f59e0b; }
`;

const Logo = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 16px;
  background: ${({ bg }) => bg || '#f3f4f6'};
  color: ${({ color }) => color || '#111827'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.5rem 0 0.25rem;
  font-weight: 800;
  font-size: 0.85rem;
`;

const Title = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const TypeLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 0.5rem;
  width: 100%;
  margin-top: 0.5rem;
`;

const Stat = styled.div`
  flex: 1;
  background: #f9fafb;
  border-radius: 8px;
  padding: 0.45rem 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StatLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.65rem;
  color: #9ca3af;
  font-weight: 500;

  & svg { width: 11px; height: 11px; }
`;

const StatValue = styled.span`
  font-size: 0.82rem;
  font-weight: 700;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  width: 100%;
  padding: 0.35rem 0.5rem;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 600;
  margin-top: 0.4rem;
  background: ${({ status }) =>
    status === 'ok' ? '#dcfce7' : status === 'warn' ? '#fef3c7' : status === 'error' ? '#fee2e2' : '#f3f4f6'};
  color: ${({ status }) =>
    status === 'ok' ? '#166534' : status === 'warn' ? '#92400e' : status === 'error' ? '#991b1b' : '#6b7280'};
`;

const AddRow = styled.button`
  width: 100%;
  max-width: 1200px;
  margin-top: 1rem;
  background: #ffffff;
  border: 2px dashed #d1d5db;
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 1rem;
  color: #6b7280;
  transition: all 0.15s ease;

  &:hover {
    border-color: #2563eb;
    color: #2563eb;
    background: #eff6ff;
  }
`;

const AddRowContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  text-align: left;
`;

const AddCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.4rem;
`;

const AddTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: #4b5563;
`;

const AddDesc = styled.div`
  font-size: 0.78rem;
  color: #9ca3af;
  line-height: 1.5;
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  max-width: 1200px;
  margin-top: 1.5rem;
  padding: 1rem 1.25rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;

  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    text-align: center;
  }
`;

const FooterText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;

  & svg {
    width: 22px;
    height: 22px;
    color: #2563eb;
    flex-shrink: 0;
  }
`;

const FooterTextBody = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;

  & strong {
    font-size: 0.88rem;
    color: #111827;
    font-weight: 700;
  }

  & span {
    font-size: 0.78rem;
    color: #6b7280;
    margin-top: 1px;
  }
`;

const GuideButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 0.95rem;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  text-decoration: none;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    border-color: #2563eb;
    color: #2563eb;
  }

  & svg { width: 13px; height: 13px; }
`;

const StarSvg = ({ filled }) => (
  <svg viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const PlusSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const HddSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12H2" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
    <circle cx="6.5" cy="16.5" r="1" />
  </svg>
);

const FileSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const ServerSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" />
    <line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);

const InfoSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const GridIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const ListIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const ExternalSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

function getDriveStyle(drive) {
  const type = (drive.type || '').toLowerCase();
  const name = (drive.name || '').toLowerCase();

  // Hetzner를 가장 먼저 체크 (HETZNER_S3 가 'aws/s3' 매치 방지)
  if (type.includes('hetzner') || name.includes('hetzner')) {
    return { bg: '#fef2f2', color: '#d50c2d', label: 'Hetzner', logo: 'hetzner' };
  }
  if (type.includes('cloudflare') || type.includes('r2') || name.includes('cloudflare') || name.includes('r2')) {
    return { bg: '#fff7ed', color: '#f38020', label: 'Cloudflare R2', logo: 'cloudflare' };
  }
  if (type === 'aws_s3' || type.includes('aws') || (type.endsWith('_s3') && !type.includes('hetzner'))) {
    return { bg: '#fff7ed', color: '#ff9900', label: 'Amazon S3', logo: 'aws' };
  }
  if (type.includes('gcs') || type.includes('google')) {
    return { bg: '#eff6ff', color: '#4285F4', label: 'GCS', logo: 'google' };
  }
  if (name.includes('cinetree') || name.includes('skynet') || type.includes('nas')) {
    return { bg: '#f5f3ff', color: '#7c3aed', label: 'NAS', logo: 'nas' };
  }
  if (type.includes('ftp')) {
    return { bg: '#ecfeff', color: '#0891b2', label: 'FTP', logo: 'ftp' };
  }
  if (type.includes('local')) {
    return { bg: '#f3f4f6', color: '#0078d4', label: '로컬 저장소', logo: 'local' };
  }
  if (type === 'default') {
    return { bg: '#f5f3ff', color: '#7c3aed', label: 'File Server', logo: 'default' };
  }
  return { bg: '#f3f4f6', color: '#374151', label: drive.type || 'Storage', logo: 'default' };
}

const ProviderLogo = ({ logo }) => {
  switch (logo) {
    case 'aws':
      return (
        <svg viewBox="0 0 64 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: 44, height: 22 }}>
          <text x="0" y="22" fontFamily="Arial, sans-serif" fontWeight="800" fontSize="22" fill="#252F3E">aws</text>
          <path d="M30 26 Q42 31 56 26" stroke="#FF9900" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </svg>
      );
    case 'cloudflare':
      return (
        <svg viewBox="0 0 64 32" fill="none" style={{ width: 44, height: 22 }}>
          <path d="M48 14c-1-4-5-7-9-7-4 0-7 2-9 5-1 0-2-1-3-1-4 0-7 3-7 7 0 1 0 1 0 2h28c0-1 1-2 1-3 0-2-1-3-1-3z" fill="#F38020" />
          <path d="M48 14h-3l-1 9h5c2 0 4-2 4-4 0-3-2-5-5-5z" fill="#FAAD3F" />
        </svg>
      );
    case 'google':
      return (
        <svg viewBox="0 0 24 24" style={{ width: 32, height: 32 }}>
          <path fill="#4285F4" d="M12 4l8 14H4z" />
          <path fill="#34A853" d="M12 20l-8-2 4-7z" opacity="0.7" />
          <circle cx="12" cy="12" r="3" fill="#FBBC05" />
        </svg>
      );
    case 'hetzner':
      return (
        <svg viewBox="0 0 32 32" style={{ width: 32, height: 32 }}>
          <rect x="2" y="2" width="28" height="28" rx="4" fill="#D50C2D" />
          <text x="16" y="22" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="800" fontSize="18" fill="#fff">H</text>
        </svg>
      );
    case 'nas':
      return (
        <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 28, height: 28 }}>
          <rect x="3" y="6" width="26" height="6" rx="1" />
          <rect x="3" y="14" width="26" height="6" rx="1" />
          <rect x="3" y="22" width="26" height="6" rx="1" />
          <line x1="7" y1="9" x2="7.01" y2="9" />
          <line x1="7" y1="17" x2="7.01" y2="17" />
          <line x1="7" y1="25" x2="7.01" y2="25" />
        </svg>
      );
    case 'ftp':
      return (
        <svg viewBox="0 0 32 32" style={{ width: 30, height: 30 }}>
          <rect x="2" y="6" width="28" height="20" rx="3" fill="currentColor" opacity="0.15" />
          <text x="16" y="20" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="800" fontSize="11" fill="currentColor">FTP</text>
        </svg>
      );
    case 'local':
      return (
        <svg viewBox="0 0 32 32" style={{ width: 28, height: 28 }}>
          <rect x="3" y="3" width="12" height="12" fill="#F25022" />
          <rect x="17" y="3" width="12" height="12" fill="#7FBA00" />
          <rect x="3" y="17" width="12" height="12" fill="#00A4EF" />
          <rect x="17" y="17" width="12" height="12" fill="#FFB900" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 26, height: 26 }}>
          <rect x="2" y="2" width="20" height="8" rx="2" />
          <rect x="2" y="14" width="20" height="8" rx="2" />
          <line x1="6" y1="6" x2="6.01" y2="6" />
          <line x1="6" y1="18" x2="6.01" y2="18" />
        </svg>
      );
  }
};

function formatBytes(bytes) {
  if (bytes == null || isNaN(bytes)) return '-';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(2)} ${units[i]}`;
}

function loadList(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function saveList(key, list) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch {}
}

const FAV_KEY = 'antsnest.remote.favorites';
const RECENT_KEY = 'antsnest.remote.recent';

function getDriveCategory(drive) {
  const style = getDriveStyle(drive);
  if (['aws', 'cloudflare', 'google'].includes(style.logo)) return 'cloud';
  if (['hetzner', 'default', 'nas'].includes(style.logo)) return 'server';
  if (['ftp'].includes(style.logo)) return 'network';
  if (['local'].includes(style.logo)) return 'local';
  return 'server';
}

const FILTER_TABS = [
  { key: 'all', label: '전체' },
  { key: 'cloud', label: '클라우드' },
  { key: 'server', label: '서버/스토리지' },
  { key: 'network', label: '네트워크' },
  { key: 'local', label: '로컬' },
];

const RemotePicker = ({ drives = [], selectedRemote, onRemoteChange }) => {
  const [stats, setStats] = useState({});
  const [favs, setFavs] = useState(() => loadList(FAV_KEY, []));
  const [recent, setRecent] = useState(() => loadList(RECENT_KEY, []));
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid');
  const [logs, setLogs] = useState([]);

  const pushLog = useCallback((tone, tag, msg) => {
    const now = new Date();
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    setLogs((prev) => [{ time, tone, tag, msg }, ...prev].slice(0, 60));
  }, []);

  const fetchStats = useCallback(async (drive) => {
    if (!drive?.fileList) return;
    const startedAt = Date.now();
    pushLog('info', 'GET', `${drive.name} /list`);
    try {
      const res = await fetch(drive.fileList);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      const files = data.files || [];
      const totalSize = files.reduce((s, f) => s + (f.size || 0), 0);
      const elapsed = Date.now() - startedAt;
      let status = 'ok';
      if (elapsed > 4000) status = 'warn';
      setStats((prev) => ({
        ...prev,
        [drive.name]: {
          totalSize,
          fileCount: typeof data.count === 'number' ? data.count : files.length,
          status,
          elapsed,
        },
      }));
      const tone = status === 'warn' ? 'warn' : 'ok';
      const tag = status === 'warn' ? 'SLOW' : '200';
      pushLog(tone, tag, `${drive.name} · ${elapsed}ms · ${files.length} files`);
    } catch (e) {
      setStats((prev) => ({
        ...prev,
        [drive.name]: { status: 'error', error: e.message },
      }));
      pushLog('error', 'ERR', `${drive.name} · ${e.message}`);
    }
  }, [pushLog]);

  useEffect(() => {
    if (drives.length > 0) {
      pushLog('info', 'BOOT', `원격지 ${drives.length}개 검색됨`);
    }
    drives.forEach((d) => {
      if (!stats[d.name]) fetchStats(d);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drives]);

  const handlePick = (drive) => {
    pushLog('info', 'PICK', `${drive.name} 선택됨`);
    if (onRemoteChange) onRemoteChange(drive);
    const next = [drive.name, ...recent.filter((n) => n !== drive.name)].slice(0, 5);
    setRecent(next);
    saveList(RECENT_KEY, next);
  };

  const toggleFav = (e, name) => {
    e.stopPropagation();
    const next = favs.includes(name) ? favs.filter((n) => n !== name) : [...favs, name];
    setFavs(next);
    saveList(FAV_KEY, next);
  };

  const recentDrives = useMemo(
    () => recent.map((n) => drives.find((d) => d.name === n)).filter(Boolean),
    [recent, drives]
  );
  const favoriteDrives = useMemo(
    () => favs.map((n) => drives.find((d) => d.name === n)).filter(Boolean),
    [favs, drives]
  );

  const recommendedName = drives[0]?.name;

  const visibleDrives = useMemo(() => {
    let list = drives;
    if (filter !== 'all') list = list.filter((d) => getDriveCategory(d) === filter);
    const sorted = [...list].sort((a, b) => {
      if (sortBy === 'size') {
        const sa = stats[a.name]?.totalSize ?? -1;
        const sb = stats[b.name]?.totalSize ?? -1;
        return sb - sa;
      }
      if (sortBy === 'count') {
        const ca = stats[a.name]?.fileCount ?? -1;
        const cb = stats[b.name]?.fileCount ?? -1;
        return cb - ca;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
    return sorted;
  }, [drives, filter, sortBy, stats]);

  return (
    <Page>
      <SidePanel>
        <Section>
          <SectionTitle>최근 접속</SectionTitle>
          {recentDrives.length > 0 ? (
            <SectionBody>
              {recentDrives.map((d) => {
                const style = getDriveStyle(d);
                const s = stats[d.name];
                return (
                  <SmallCard key={`r-${d.name}`} onClick={() => handlePick(d)}>
                    <SmallIcon bg={style.bg} color={style.color}>
                      <ProviderLogo logo={style.logo} />
                    </SmallIcon>
                    <SmallText>
                      <SmallName>{d.name}</SmallName>
                      <SmallSub>
                        {style.label}{s?.totalSize != null ? ` · ${formatBytes(s.totalSize)}` : ''}
                      </SmallSub>
                    </SmallText>
                    <StatusDot status={s?.status} />
                  </SmallCard>
                );
              })}
            </SectionBody>
          ) : (
            <EmptySection>
              <EmptyStarIcon><StarSvg /></EmptyStarIcon>
              <EmptyTitle>아직 접속한 원격지가<br />없습니다.</EmptyTitle>
              <EmptyDesc>원격지를 선택하면<br />최근 접속 목록이 표시됩니다.</EmptyDesc>
            </EmptySection>
          )}
        </Section>

        <Section>
          <SectionTitle>즐겨찾기</SectionTitle>
          {favoriteDrives.length > 0 ? (
            <SectionBody>
              {favoriteDrives.map((d) => {
                const style = getDriveStyle(d);
                return (
                  <SmallCard key={`f-${d.name}`} onClick={() => handlePick(d)}>
                    <SmallIcon bg={style.bg} color={style.color}>
                      <ProviderLogo logo={style.logo} />
                    </SmallIcon>
                    <SmallText>
                      <SmallName>{d.name}</SmallName>
                      <SmallSub>{style.label}</SmallSub>
                    </SmallText>
                    <StarBtn on={1} onClick={(e) => toggleFav(e, d.name)} aria-label="즐겨찾기 해제">
                      <StarSvg filled />
                    </StarBtn>
                  </SmallCard>
                );
              })}
            </SectionBody>
          ) : (
            <EmptySection>
              <EmptyStarIcon><StarSvg /></EmptyStarIcon>
              <EmptyTitle>즐겨찾기가 없습니다.</EmptyTitle>
              <EmptyDesc>원격지 카드의 ☆ 버튼을<br />클릭하여 추가할 수 있습니다.</EmptyDesc>
            </EmptySection>
          )}
        </Section>

        <Section style={{ marginTop: 'auto' }}>
          <SectionTitle>시스템 로그</SectionTitle>
          <LogPanel>
            <LogTitle>
              <span>$ antsnest --watch</span>
              <LogTitleDot />
            </LogTitle>
            {logs.length === 0 ? (
              <LogRow><LogTime>--:--:--</LogTime><LogTag>···</LogTag><LogMsg>대기 중</LogMsg></LogRow>
            ) : (
              logs.slice(0, 8).map((l, idx) => (
                <LogRow key={idx}>
                  <LogTime>{l.time}</LogTime>
                  <LogTag tone={l.tone}>{l.tag}</LogTag>
                  <LogMsg>{l.msg}</LogMsg>
                </LogRow>
              ))
            )}
          </LogPanel>
        </Section>

        <AddBtn onClick={() => alert('원격지 연결 추가 (구현 예정)')}>
          <PlusSvg /> 원격지 연결 추가
        </AddBtn>
      </SidePanel>

      <Main>
        <Hero>
          <HeroTitle>원격지를 선택해주세요</HeroTitle>
          <HeroDesc>
            파일을 관리할 원격지(버킷, 서버, 스토리지)를 선택하면 해당 위치의 폴더와 파일 목록을 확인할 수 있습니다.
          </HeroDesc>
        </Hero>

        <ControlRow>
          <FilterTabs>
            {FILTER_TABS.map((t) => (
              <FilterTab
                key={t.key}
                active={filter === t.key ? 1 : 0}
                onClick={() => setFilter(t.key)}
              >
                {t.label}
              </FilterTab>
            ))}
          </FilterTabs>
          <ControlRight>
            <SortSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="name">이름순</option>
              <option value="size">용량순</option>
              <option value="count">파일 수</option>
            </SortSelect>
            <ViewToggleGroup>
              <ViewToggleBtn
                active={viewMode === 'grid' ? 1 : 0}
                onClick={() => setViewMode('grid')}
                aria-label="그리드"
              >
                <GridIcon />
              </ViewToggleBtn>
              <ViewToggleBtn
                active={viewMode === 'list' ? 1 : 0}
                onClick={() => setViewMode('list')}
                aria-label="리스트"
              >
                <ListIcon />
              </ViewToggleBtn>
            </ViewToggleGroup>
          </ControlRight>
        </ControlRow>

        <Grid list={viewMode === 'list' ? 1 : 0}>
          {visibleDrives.map((d) => {
            const style = getDriveStyle(d);
            const s = stats[d.name];
            const isActive = selectedRemote?.name === d.name;
            const isFav = favs.includes(d.name);
            const isRec = d.name === recommendedName;

            return (
              <Card key={d.name} active={isActive ? 1 : 0} onClick={() => handlePick(d)}>
                {isRec && <RecBadge>추천</RecBadge>}
                <StarTopRight on={isFav ? 1 : 0} onClick={(e) => toggleFav(e, d.name)} aria-label="즐겨찾기">
                  <StarSvg filled={isFav} />
                </StarTopRight>
                <Logo bg={style.bg} color={style.color}>
                  <ProviderLogo logo={style.logo} />
                </Logo>
                <Title>{d.name}</Title>
                <TypeLabel>{style.label}</TypeLabel>

                <StatsRow>
                  <Stat>
                    <StatLabel><HddSvg /> 용량</StatLabel>
                    <StatValue>{s?.totalSize != null ? formatBytes(s.totalSize) : (s?.status === 'error' ? '-' : '...')}</StatValue>
                  </Stat>
                  <Stat>
                    <StatLabel><FileSvg /> 파일 수</StatLabel>
                    <StatValue>{s?.fileCount != null ? `${s.fileCount.toLocaleString()} 개` : (s?.status === 'error' ? '-' : '...')}</StatValue>
                  </Stat>
                </StatsRow>

                <StatusPill status={s?.status}>
                  <StatusDot status={s?.status} />
                  {s?.status === 'ok' ? '연결됨'
                    : s?.status === 'warn' ? '느린 연결'
                    : s?.status === 'error' ? '연결 끊김'
                    : '확인 중...'}
                </StatusPill>
              </Card>
            );
          })}
        </Grid>

        <AddRow onClick={() => alert('원격지 연결 추가 (구현 예정)')}>
          <AddRowContent>
            <AddCircle><PlusSvg /></AddCircle>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <AddTitle>원격지 추가</AddTitle>
              <AddDesc>새로운 스토리지 또는 서버를 연결하여 관리할 수 있습니다.</AddDesc>
            </div>
          </AddRowContent>
        </AddRow>

        <FooterBar>
          <FooterText>
            <InfoSvg />
            <FooterTextBody>
              <strong>원하는 원격지가 보이지 않나요?</strong>
              <span>다양한 스토리지(S3, FTP, NAS, WebDAV 등)를 추가하여 통합 관리할 수 있습니다.</span>
            </FooterTextBody>
          </FooterText>
          <GuideButton href="#guide" onClick={(e) => { e.preventDefault(); alert('가이드 (구현 예정)'); }}>
            연결 가이드 보기 <ExternalSvg />
          </GuideButton>
        </FooterBar>
      </Main>
    </Page>
  );
};

export default RemotePicker;
